import type {
  AdminAction,
  AdminMe,
  AdminRow,
  AuditRow,
  OperatorRow,
  OverviewData,
  PagedResult,
  RiskRow,
  TreasureRow,
  VoucherRow,
  VoucherTemplateRow,
} from '../types'

const now = Date.now()

export const demoEnabled = import.meta.env.DEV
  && new URLSearchParams(window.location.search).get('demo') === '1'

const demoMe: AdminMe = {
  uid: 'demo-admin',
  displayName: '林野 · 运营值班',
  roles: ['admin_super'],
  capabilities: [
    'overview.read', 'treasures.read', 'treasures.write',
    'voucherTemplates.read', 'voucherTemplates.write', 'vouchers.read',
    'vouchers.fulfill', 'vouchers.admin', 'operators.read', 'operators.write',
    'admins.read', 'admins.write', 'audit.read', 'risk.read',
  ],
  expiresAt: 0,
}

const treasures: TreasureRow[] = [
  {
    id: 't-west-lake', title: '曲院风荷 · 铜色坐标', mode: 'qr_voucher', status: 'active',
    ownerName: '官方寻宝员 07', ownerIdMasked: 'oX8f…2Ja', latitude: 30.251, longitude: 120.133,
    qrStatus: 'active', voucherTemplateId: 'vt-badge', foundCount: 17, reportCount: 0,
    createdAt: now - 7200000, updatedAt: now - 360000,
  },
  {
    id: 't-canal', title: '运河桥下的旧船票', mode: 'qr_physical', status: 'pending_activation',
    ownerName: '官方寻宝员 03', ownerIdMasked: 'oP11…8Ks', latitude: 30.319, longitude: 120.148,
    qrStatus: 'pending_activation', voucherTemplateId: '', foundCount: 0, reportCount: 0,
    createdAt: now - 13800000, updatedAt: now - 13800000,
  },
  {
    id: 't-alley', title: '小河直街的绿色铁盒', mode: 'physical', status: 'under_review',
    ownerName: '社区用户', ownerIdMasked: 'oV5c…1Qz', latitude: 30.307, longitude: 120.137,
    qrStatus: '', voucherTemplateId: '', foundCount: 0, reportCount: 2,
    createdAt: now - 86400000, updatedAt: now - 1800000,
  },
]

const templates: VoucherTemplateRow[] = [
  {
    id: 'vt-badge', title: '西湖城市徽章', status: 'active', rewardDescription: '限量珐琅徽章一枚',
    redemptionLocation: '杭州城市活动站', redemptionInstructions: '出示兑换券并核对编号',
    stockAvailable: 38, stockTotal: 120, validDays: 14, partnerId: 'official-hz',
    allowedChannels: ['customer_service', 'operator_scan'], createdAt: now - 1209600000, updatedAt: now - 5400000,
  },
  {
    id: 'vt-postcard', title: '运河手绘明信片', status: 'draft', rewardDescription: '运河主题明信片一套',
    redemptionLocation: '运河游客中心', redemptionInstructions: '联系客服预约领取',
    stockAvailable: 80, stockTotal: 80, validDays: 30, partnerId: 'canal-partner',
    allowedChannels: ['customer_service'], createdAt: now - 172800000, updatedAt: now - 10800000,
  },
]

const vouchers: VoucherRow[] = [
  {
    id: 'voucher-0000000001', displayCode: 'VOUCHER-00', templateId: 'vt-badge', treasureId: 't-west-lake',
    title: '西湖城市徽章', status: 'fulfillment_pending', holder: { code: '92AF…17C', nickname: '山止川行' },
    partnerId: 'official-hz', allowedChannels: ['customer_service', 'operator_scan'], redemptionChannel: '',
    issuedAt: now - 840000, expiresAt: now + 1209600000, redeemedAt: 0, version: 2, fulfillment: null,
  },
  {
    id: 'voucher-0000000002', displayCode: 'VOUCHER-00', templateId: 'vt-badge', treasureId: 't-west-lake',
    title: '西湖城市徽章', status: 'issued', holder: { code: 'A104…88D', nickname: '拾光者' },
    partnerId: 'official-hz', allowedChannels: ['customer_service'], redemptionChannel: '',
    issuedAt: now - 4200000, expiresAt: now + 604800000, redeemedAt: 0, version: 1, fulfillment: null,
  },
]

const operators: OperatorRow[] = [
  { id: 'op-ref-1', openidMasked: 'oX8f…2Ja', displayName: '现场组 · 阿禾', active: true, roles: ['qr_treasure_publisher', 'voucher_redeemer'], partnerIds: ['official-hz'], templateIds: [], expiresAt: 0, updatedAt: now - 3600000 },
  { id: 'op-ref-2', openidMasked: 'oP11…8Ks', displayName: '客服组 · 小屿', active: true, roles: ['customer_service'], partnerIds: [], templateIds: ['vt-badge'], expiresAt: now + 2592000000, updatedAt: now - 7200000 },
]

const admins: AdminRow[] = [
  { id: 'demo-admin', uidMasked: 'demo…min', displayName: '林野 · 运营值班', active: true, roles: ['admin_super'], expiresAt: 0, updatedAt: now - 600000 },
  { id: 'support-admin', uidMasked: 'supp…min', displayName: '客服席位 01', active: true, roles: ['admin_customer_service'], expiresAt: 0, updatedAt: now - 86400000 },
]

const audit: AuditRow[] = [
  { id: 'audit-1', type: 'voucher_fulfillment_started', action: 'admin.vouchers.startFulfillment', actorType: 'web_admin', actorMasked: 'supp…min', objectType: 'voucher', objectId: 'voucher-0000000001', reason: '', before: { status: 'issued' }, after: { status: 'fulfillment_pending' }, createdAt: now - 780000 },
  { id: 'audit-2', type: 'treasure_qr_activated', action: 'treasure.activateQr', actorType: 'miniprogram', actorMasked: 'oX8f…2Ja', objectType: 'treasure', objectId: 't-west-lake', reason: '', before: null, after: { status: 'active' }, createdAt: now - 7200000 },
]

const risks: RiskRow[] = [
  { id: 'o91A…2Qx', failureCount: 8, blockedUntil: now + 420000, rapidLocationChange: false, note: '', updatedAt: now - 120000 },
  { id: 'o07P…9Wc', failureCount: 3, blockedUntil: 0, rapidLocationChange: true, note: '待核对活动现场网络', updatedAt: now - 1800000 },
]

function page<T>(items: T[]): PagedResult<T> {
  return { items, limit: 20, offset: 0, hasMore: false }
}

export function demoAdminCall<T>(action: AdminAction): T {
  const data: Record<AdminAction, unknown> = {
    'admin.me': demoMe,
    'admin.overview': { generatedAt: now, metrics: { treasures: 47, pendingQr: 3, activeTemplates: 2, pendingVouchers: 6, capturesToday: 18, riskStates: 2 } } satisfies OverviewData,
    'admin.treasures.list': page(treasures),
    'admin.treasures.get': { treasure: treasures[0], qr: { status: 'active', version: 1 }, voucherTemplate: templates[0], recentCaptures: [] },
    'admin.treasures.setStatus': { status: 'disabled' },
    'admin.treasures.qrAsset': { treasureId: treasures[0].id, status: 'active', imageUrl: '' },
    'admin.voucherTemplates.list': page(templates),
    'admin.voucherTemplates.upsert': { created: true },
    'admin.voucherTemplates.setStatus': { status: 'active' },
    'admin.voucherTemplates.adjustStock': { stockAvailable: 41 },
    'admin.vouchers.list': page(vouchers),
    'admin.vouchers.get': { voucher: vouchers[0], events: audit },
    'admin.vouchers.startFulfillment': { status: 'fulfillment_pending' },
    'admin.vouchers.markFulfilled': { status: 'redeemed' },
    'admin.vouchers.void': { status: 'voided' },
    'admin.vouchers.correct': { correctionRecorded: true },
    'admin.operators.list': page(operators),
    'admin.operators.upsert': { updated: true },
    'admin.operators.disable': { disabled: true },
    'admin.admins.list': page(admins),
    'admin.admins.upsert': { updated: true },
    'admin.admins.disable': { disabled: true },
    'admin.audit.list': page(audit),
    'admin.risk.list': page(risks),
  }
  return data[action] as T
}

export function demoIdentity(): AdminMe {
  return demoMe
}
